// apps/web/src/components/creators/CreatorDiscovery.tsx
// Creator discovery and search functionality

import type React from 'react';
import { useState, useEffect } from 'react';
import type { Creator } from '../../types/creator';
import { apiClient } from '../../lib/apiClient';

export function CreatorDiscovery() {
  const [creators, setCreators] = useState<Creator[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState({
    searchTerm: '',
    creatorType: '',
    location: '',
    specialties: [] as string[]
  });

  useEffect(() => {
    const searchCreators = async () => {
      try {
        setLoading(true);
        // This would eventually use the search API with filters
        const response = await apiClient.get('/api/v1/creators');
        setCreators(response.data.creators);
      } catch (err) {
        setError('Failed to load creators');
        console.error('Error loading creators:', err);
      } finally {
        setLoading(false);
      }
    };

    searchCreators();
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    // Search implementation would go here
    console.log('Searching with filters:', filters);
  };

  const handleFilterChange = (key: string, value: string | string[]) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  if (loading) {
    return <div className="loading">Loading creators...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <div className="creator-discovery">
      <div className="search-filters">
        <form onSubmit={handleSearch}>
          <input
            type="text"
            placeholder="Search creators..."
            value={filters.searchTerm}
            onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
          />
          <select
            value={filters.creatorType}
            onChange={(e) => handleFilterChange('creatorType', e.target.value)}
          >
            <option value="">All Types</option>
            <option value="INDIVIDUAL">Individual</option>
            <option value="CREATOR_BUSINESS">Creator Business</option>
            <option value="CREATOR_COLLECTIVE">Creator Collective</option>
            <option value="MEDIA_CREATOR">Media Creator</option>
            <option value="VIRTUAL_CREATOR">Virtual Creator</option>
          </select>
          <button type="submit" className="btn btn-primary">Search</button>
        </form>
      </div>

      <div className="creators-grid">
        {creators.map(creator => (
          <CreatorCard key={creator.id} creator={creator} />
        ))}
      </div>
    </div>
  );
}

// Helper component
function CreatorCard({ creator }: { creator: Creator }) {
  return (
    <div className="creator-card">
      <div className="creator-header">
        <h3>{creator.displayName}</h3>
        <span className="creator-type">{creator.creatorType}</span>
      </div>
      <p>{creator.bio || 'No bio available'}</p>
      <div className="creator-stats">
        <span>Followers: {creator.followers || 0}</span>
        <span>Engagement: {(creator.engagementRate || 0).toFixed(1)}%</span>
      </div>
      <div className="actions">
        <button className="btn btn-outline">View Profile</button>
        <button className="btn btn-primary">Connect</button>
      </div>
    </div>
  );
}